import { useDispatch, useSelector } from "react-redux";
import useLabels from "../../hooks/useLabels";
import SecondaryBar from "../SecondaryBar";
import {
  selectActiveWinery,
  selectConfigs,
  selectExpiredOperations,
  selectExpiringOperations,
  selectLots,
  selectNextOperations,
  selectTanks,
  selectToBeCompletedOperations,
  selectUsers,
  selectAnalysis,
  selectTreatments,
  selectOperations,
  selectApplications,
  selectWineMaking,
} from "../../context/selectors";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { lazy, Suspense, useEffect, useState } from "react";
import { addDays, addMonths, format, isToday } from "date-fns";
import { NavLink, useNavigate } from "react-router-dom";
import {
  BatchColumn,
  CTA,
  CellarmanColumn,
  Dropdown,
  TankColumn,
  BottlingColumn,
  CloseColumn,
  OperationStatusColumn,
} from "../Generic";
import {
  getExpiredOperations,
  getLots,
  getOperations,
  getPDF,
  getProtocols,
  getWineMakingProtocols,
  getProtocolApplications,
  getTanks,
  getUsers,
} from "../../services/utils";
import { setTanks } from "../../context/tanks/tanksSlice";
import { setUsers } from "../../context/users/usersSlice";
import { setOperations } from "../../context/operations/operationsSlice";
import { setLots } from "../../context/lots/lotsSlice";
import { setApplications } from "../../context/protocols/applicationSlice";
import { getApplicationColor } from "../../services/common";

import "./styles/index.scss";
import ReactDatePicker from "react-datepicker";
import useAuth from "../../hooks/useAuth";
import { ROLES } from "../../constants/base";
// import TableEdit from "../TableEdit";
import { createColumnHelper } from "@tanstack/react-table";
import TableCell from "../TableEdit/TableCell";
// import { useMedia } from "react-use";
import { setAnalysis } from "../../context/protocols/analysisSlice";
import { setTreatments } from "../../context/protocols/treatmentsSlice";
import { setExpOperations } from "../../context/expOperations/expOperationsSlice";
import { SkeletonTableEdit } from "../Skeleton/Skeleton";
import { computeOperationRow, getTreatmentTimingDisplay, getStartedOperationTimingInfo } from "../../services/common";
import { Icon } from "../../components/Icon";
import TabSelector from "../TabSelector";
import ProtocolApplications from "../ProtocolApplications";
import { getItem, setItem } from "../../services/LocalStorage";
import { setAppTemplates } from "../../context/protocols/appTemplateSlice";

const TableEdit = lazy(() => import("../TableEdit"));

const OperationsHub = ({ isMobile }) => {
  // const isMobile = useMedia("(max-width: 480px)");
  // const isTablet = useMedia("(min-width: 481px) and (max-width: 736px)");
  const configs = useSelector(selectConfigs);
  const [dates, setDates] = useState({
    startDate: new Date(),
    endDate: addMonths(
      new Date(),
      configs.settings.nextOperationMonthsInFutureDefault || 3
    ),
  });
  const [activeTab, setActiveTab] = useState(() => getItem("activeTab-operationsHub") || "all");
  const analysis = useSelector(selectAnalysis);
  const treatments = useSelector(selectTreatments);
  const [getLabel] = useLabels();
  const activeWinery = useSelector(selectActiveWinery);
  // const operations2 = useSelector((state) =>
  //   selectNextOperations(state, {
  //     startDate: dates.startDate,
  //     endDate: dates.endDate,
  //   })
  // );
  const operations = useSelector(selectOperations);
  const toBeDoneOperations = useSelector(selectToBeCompletedOperations);
  const expiringOperations = useSelector(selectExpiringOperations);
  const expiredOperations = useSelector(selectExpiredOperations);
  const applications = useSelector(selectApplications);
  const wineMaking = useSelector(selectWineMaking);
   // const [expiredOperations, setExpiredOperations] = useState([]);
  // const expiredOperations = useSelector(selectExpiredOperations);
  const users = useSelector(selectUsers);
  const tanks = useSelector(selectTanks);
  const lots = useSelector(selectLots);
  const dispatch = useDispatch();
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const role = auth?.role || ROLES.GUEST;
  const user_id = auth?.user_id || null;

  useEffect(() => {
    // find min and max dates
    const dates = [...expiredOperations, ...operations]?.map((op) => new Date(op?.expire_date));
    if (!dates.length) return;

    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));

    setDates({
      startDate: addDays(minDate.setHours(0, 0, 0), -1),
      endDate: addMonths(
        maxDate.setHours(0, 0, 0),
        configs.settings.nextOperationMonthsInFutureDefault || 3
      ),
    });
  }, [operations, expiredOperations]);

  useEffect(() => {
    setItem("activeTab-operationsHub", activeTab);
  }, [activeTab]);

  const sameDay = (d1, d2) => {
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  };

  useEffect(() => {
    if (!treatments || treatments.length === 0) {
      loadTreatments();
    }
    if (!analysis || analysis.length === 0) {
      loadAnalysis();
    }
  }, []);

  const loadAnalysis = async () => {
    const currentAnalysis = await getProtocols("analysis", axiosPrivate);
    dispatch(setAnalysis(currentAnalysis));
  };

  const loadTreatments = async () => {
    const currentTreatments = await getProtocols("treatment", axiosPrivate);
    dispatch(setTreatments(currentTreatments));
  };

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor(
      (row) => computeOperationRow(row, treatments, analysis, getLabel),
        // row.type === 'ADD' ? computeAddOpTypeString(row) : 
        // getLabel(`operationType${row.type}`) +
        // computeAdditionalTypeString(row) +
        // computeRecurrentString(row),
      {
        header: getLabel("operationType"),
        cell: TableCell,
        meta: {
          type: "operationType",
          filterVariant: "select",
          selector: "type",
          additional: "opWithStart"
        },
      }
    ),
    columnHelper.accessor(
      (row) => getLabel(`operationPriorityLong${row.priority}`),
      {
        header: getLabel("priority"),
        cell: TableCell,
        meta: {
          filterVariant: "select",
        },
        sortingFn: "PrioritySortingFn",
      }
    ),
    columnHelper.accessor(
      (row) => {
        const timing = getTreatmentTimingDisplay(row, configs, getLabel);
        if (timing.isTimed) {
          return timing.dateRange;
        }
        return row?.expire_date
          ? format(
              new Date(row.expire_date),
              sameDay(new Date(row?.expire_date), new Date())
                ? configs.hourShortDateNoYearFormat
                : configs.shortDateNoYearFormat
            )
          : "";
      },
      {
        // TO DO short date se repetitive nell stesso giorno
        header: getLabel("expiringDate"),
        cell: (props) => {
          const timing = getTreatmentTimingDisplay(props.row.original, configs, getLabel);
          const expireDate = new Date(props.row.original?.expire_date);
          const today = new Date();

          // Check if operation needs start_date (not yet started)
          const isStabilizationOrFiltering = [
            "FILTERING",
            "STABILIZATION_STATIC",
            "STABILIZATION_DYNAMIC",
            "STABILIZATION_RESIN",
            "HOMOGENIZATION_MASS",
            "OXYGENATION",
          ].includes(props.row.original?.type);
          const isNotStarted = isStabilizationOrFiltering && !props.row.original?.start_date;
          const isStarted = props.row.original?.start_date;

          // Show as expired only if not yet started and date is in past
          const isExpired = !isStarted && expireDate < today;

          // Check if expires today
          const isExpiringToday = isToday(expireDate);

          // Determine badge class and icon
          let badgeClass = "normal";
          let iconType = isNotStarted ? "fa-clock" : isStarted ? "fa-spinner" : "fa-calendar-day";

          if (isExpired) {
            badgeClass = "expired";
          } else if (isStarted) {
            badgeClass = "started";
          } else if (isExpiringToday) {
            badgeClass = "warning";
          }

          // Calculate timing info for started operations
          let sublabelText = timing.sublabel;
          if (isStarted) {
            const timingInfo = getStartedOperationTimingInfo(props.row.original);
            const timingParts = [];
            if (timingInfo.elapsed) {
              timingParts.push(`Trascorso: ${timingInfo.elapsed}`);
            }
            if (timingInfo.remaining) {
              timingParts.push(`Rimanente: ${timingInfo.remaining}`);
            }
            if (timingParts.length > 0) {
              sublabelText = timingParts.join(" · ");
            }
          }

          return (
            <div className="expiring_date_container">
              <div className={`expiring_date_badge ${badgeClass}`}>
                <Icon icon={iconType} size="sm" spin={isStarted} style={isStarted ? { color: "#3498db" } : undefined} />
                <span>{timing.dateRange}</span>
              </div>
              {sublabelText && (
                <div className="expiring_date_sublabel">
                  {sublabelText}
                </div>
              )}
            </div>
          );
        },
        meta: {
          isDate: true,
          iconType: "fa-calendar-day",
          iconColor: "grey",
          colorExpired: true,
          type: "expireDate",
          dateFormat: configs.hourShortDateNoYearFormat,
          filterVariant: "date",
          dateField: "expire_date",
        },
        filterFn: "DateInRange",
      }
    ),
    columnHelper.accessor("operationSrcTank", {
      header: getLabel("operationSrcTank"),
      cell: (props) =>
        props.row?.original?.type === "NEW_LOT" ? (
          props.row?.original?.src_tanks?.[0]?.name || ""
        ) : (props.row?.original?.src_tanks?.find(
            (tank) =>
              tank?.batch_id != null &&
              ["ANALYSIS", "TREATMENT"].includes(props.row?.original?.type) &&
            props.row.original?.treatments?.[0]?.info?.isGrapeAnalysis)
          ) || props.row.original?.batch_id_in ? (
          <BatchColumn
            lots={lots}
            rowData={props.row?.original}
            type={props.row?.original?.type}
          />
        ) : (
          <TankColumn rowData={props.row?.original?.src_tanks} tanks={tanks} />
        ),
      meta: {
        array: "src_tanks",
        selector: "name",
        filterVariant: "text",
      },
      filterFn: "MultipleRowsFilterFn",
    }),
    columnHelper.accessor("operationDestTank", {
      header: getLabel("operationDestTank"),
      cell: (props) =>
        props.row?.original?.type === "CLOSING" ? (
          <CloseColumn rowData={props?.row?.original?.dest_tanks} />
        ) : 
        props.row?.original?.type === "BOTTLING" ? (
          <BottlingColumn rowData={props?.row?.original?.dest_tanks} />
        ) : props.row?.original?.dest_tanks?.find((tank) => tank?.batch_id && !tank?.tank_id) ? (
          <BatchColumn
            lots={lots}
            rowData={props.row?.original}
            type={props.row?.original?.type}
          />
        ) : (
          <TankColumn rowData={props.row?.original?.dest_tanks} tanks={tanks} />
        ),
      meta: {
        array: "dest_tanks",
        selector: "name",
        filterVariant: "text",
      },
      filterFn: "MultipleRowsFilterFn",
    }),

    // {
    //   Header: getLabel("operationCompletionDate"),
    //   accessor: (row) =>
    //     row.completion_date
    //       ? format(new Date(row.completion_date), configs.shortDateFormat)
    //       : "-",
    // },
    // {
    //   Header: getLabel("operationNotes"),
    //   accessor: (row) => row.notes,
    // },
  ];

  columns.push(
    columnHelper.accessor(
      (row) => {
        if (row?.protocol_application_id != null) {
          const app = applications?.find(a => a.id === row.protocol_application_id);
          return app?.protocol_name || getLabel("operationSourceApplication", { id: row.protocol_application_id });
        }
        return getLabel("operationSourceManual");
      },
      {
        header: getLabel("operationSource"),
        cell: (info) => {
          const appId = info.row.original.protocol_application_id;
          const colors = appId ? getApplicationColor(appId) : null;
          return (
            <span
              className={`source_badge ${appId ? "clickable" : ""}`}
              style={colors ? {
                backgroundColor: colors.bg,
                color: colors.text,
              } : undefined}
              onClick={(e) => {
                if (appId) {
                  e.stopPropagation();
                  navigate(`/protocol-applications/${appId}`);
                }
              }}
            >
              {info.getValue()}
            </span>
          );
        },
        meta: {
          filterVariant: "select",
          type: "text",
        },
      }
    )
  );

  if (role === ROLES.CLIENT) {
    columns.push(
      columnHelper.accessor("cellarman", {
        header: getLabel("cellarman"),
        cell: (props) => (
          <CellarmanColumn
            rowData={props.row?.original?.cellarman_ids}
            users={users}
          />
        ),
        meta: {
          array: users,
          selector: "cellarman_ids",
          filterVariant: "text",
        },
        filterFn: "MultipleRowsFilterFn",
      })
    );
  }

  const loadOperations = async () => {
    const currentOperations = await getOperations(axiosPrivate);
    dispatch(setOperations(currentOperations));
  };

  const loadWineMakingProtocols = async () => {
    const currentApplications = await getWineMakingProtocols(axiosPrivate);
    dispatch(setAppTemplates(currentApplications));
  };

  const loadApplications = async () => {
    const currentProtocolApplications = await getProtocolApplications(axiosPrivate);
    dispatch(setApplications(currentProtocolApplications || []));
  };

  const loadUsers = async () => {
    const currentUsers = await getUsers(axiosPrivate);
    dispatch(setUsers(currentUsers));
  };

  const loadTanks = async () => {
    const currentTanks = await getTanks(activeWinery?.id, axiosPrivate);
    dispatch(setTanks(currentTanks));
  };

  const loadLots = async () => {
    const currentLots = await getLots(activeWinery?.id, axiosPrivate);
    dispatch(setLots(currentLots));
  };

  const loadExpiredOperations = async () => {
    const currentExpiredOperations = await getExpiredOperations(
      activeWinery?.id,
      axiosPrivate
    );
    // setExpiredOperations([...currentExpiredOperations]);
    dispatch(setExpOperations(currentExpiredOperations));
  };

  const initData = async () => {
    const promiseArray = [];
    if (operations?.length === 0) {
      promiseArray.push(loadOperations());
    }
    if (users?.length === 0) {
      promiseArray.push(loadUsers());
    }
    if (tanks?.length === 0) {
      promiseArray.push(loadTanks());
    }
    if (lots?.length === 0) {
      promiseArray.push(loadLots());
    }
    if (expiredOperations?.length === 0) {
      promiseArray.push(loadExpiredOperations());
    }
    if (applications?.length === 0) {
      promiseArray.push(loadApplications());
    }
    if (!wineMaking || wineMaking.length === 0) {
      promiseArray.push(loadWineMakingProtocols());
    }

    return Promise.all(promiseArray);
  };

  useEffect(() => {
    initData();
  }, []);

  // useEffect(() => {
  //   if (operations?.length > 0)
  //     setAllOperations([
  //       ...operations?.filter((o) => new Date() > new Date(o?.expire_date))?.sort(function(a, b) {
  //         let adate = new Date(a?.expire_date),
  //             bdate = new Date(b.expire_date),
  //             rv = adate - bdate;
  //         if (rv === 0) {
  //             rv = b?.priority - a?.priority;
  //         }
  //         return rv;
  //       }),
  //     ...operations?.filter((o) => new Date() <= new Date(o?.expire_date))?.sort(function(a, b) {
  //       let adate = new Date(a?.expire_date),
  //           bdate = new Date(b.expire_date),
  //           rv = adate - bdate;
  //       if (rv === 0) {
  //           rv = b?.priority - a?.priority;
  //       }
  //       return rv;
  //     })
  //     ]);
  // }, [operations]);

  const requestPDF = async (expiringTodayOnly = false, expired = true) => {
    const data = {
      // pdf_type: "operations",
      pdf_type: (expiringTodayOnly && expired) ? "operationsTodayAndExpired" :
        expiringTodayOnly && !expired ? "operationsToday" : "operation",
      type: "operations",
      title: getLabel("operations"),
      rows: [activeWinery?.id, [
        ...operations?.filter(
          (op) =>
            !expiringTodayOnly ||
            new Date(op?.expire_date).setHours(0, 0, 0, 0) ==
              new Date().setHours(0, 0, 0, 0)
        ),
        ...(expired ? expiredOperations : []),
      ]?.map((operation) => operation.id)],
      columns: [
        getLabel("priority"),
        getLabel("operationType"),
        getLabel("operationSrcTank"),
        getLabel("operationDestTank"),
        ...(role === ROLES.CLIENT ? [getLabel("cellarman")] : []),
        getLabel("expiringDate"),
        // getLabel("protocolAdditional"),
        // getLabel("operationNotes"),
      ],
      headers: [
        "priority",
        "type",
        "src_tanks",
        "dest_tanks",
        ...(role === ROLES.CLIENT ? ["cellarman_ids"] : []),
        "expire_date",
        // "protocolAdditional",
        // "note",
      ],
      winery_name: activeWinery?.name || "",
      order_by: "expire_date",
      // "order_sort": "desc",
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language:
        navigator.languages?.length > 0
          ? navigator.languages[0]?.replace("_", "-")
          : "it-IT",
    };

    await getPDF(data, axiosPrivate);
  };

  const renderWarning = (type) => {
    const operations = (
      type === "tbd" ? toBeDoneOperations : expiringOperations
    )?.filter((op) => new Date(dates.startDate) < new Date(op.expire_date));
    // TO DO user settings: the user can chose to use dates.startDate or now()
    return (
      <div
        className={`warning_wrapper warning_${
          type === "exp" ? (operations.length > 0 ? "exp" : "ok") : type
        } split ${operations.length === 0 ? "is_grey" : ""}`}
      >
        {operations?.length > 0 ? (
          <div className="warning_label">
            {getLabel(
              type === "tbd" ? `operationsToBeCompleted` : "expiringOperations"
            )}
          </div>
        ) : (
          <div className="warning_label">
            {getLabel(
              type === "tbd"
                ? "noOperationsToBeCompleted"
                : "expiringOperations"
            )}
          </div>
        )}
        {operations?.length > 0 && (
          <div className="warning_value">{operations.length}</div>
        )}
        {(!operations || operations?.length === 0) && (
          <div className="warning_value">0</div>
        )}
      </div>
    );
  };

  const renderExpired = () => {
    if (expiredOperations?.length > 0)
      return (
        <div className="warning_wrapper warning_due full">
          <div className="warning_label">{getLabel("expiredOperations")}</div>
          <div className="warning_value">{expiredOperations.length}</div>
          {/* {(!operations || operations?.length === 0) && (
          <div className="warning_value">0</div>
        )} */}
        </div>
      );
  };

  const renderNavMenu = () => {
    return (
      <>
        {/* <div className="primary_container_menu"> */}
        <h2>{getLabel("programmingNavLink")}</h2>
        <div className="hub_primary_container_row cards">
          <div className="hub_primary_container_row_warning_wrapper">
            <div className="hub_primary_container_row_warning_wrapper_upper">
              <div className="modify-button">
                <NavLink to={user_id ? `/users/${user_id}/customize` : ""}>
                  <CTA>
                    <Icon icon="fa-gear" />
                  </CTA>
                </NavLink>
              </div>
              {renderWarning("tbd")}
              {expiringOperations?.length > 0 && renderWarning("exp")}
            </div>
            {renderExpired()}
          </div>
        </div>
        <div className="hub_primary_container_row no_wrap">
          {role === ROLES.CLIENT && (
            <div className="operation_dropdown">
              <Dropdown
                label={
                  <>
                    {getLabel("programOperation")}{" "}
                    <Icon icon="fa-calendar-plus" />
                  </>
                }
                options={[
                  {
                    label: getLabel("operationTypeANALYSIS"),
                    handler: () => navigate("/programming/analysis/new"),
                  },
                  {
                    label: getLabel("operationTypeCUT"),
                    handler: () => navigate("/programming/cut/new"),
                  },
                  {
                    label: getLabel("operationTypeDECANT"),
                    handler: () => navigate("/programming/decant/new"),
                  },
                  {
                    label: getLabel("operationTypeDESTEMMING"),
                    handler: () => navigate("/programming/destemming/new"),
                  },
                  {
                    label: getLabel("operationTypeRACKING"),
                    handler: () => navigate("/programming/racking/new"),
                  },
                  {
                    label: getLabel("operationTypeFILTERING"),
                    handler: () => navigate("/programming/filtering/new"),
                  },
                  {
                    label: getLabel("operationTypeTREATMENT"),
                    handler: () => navigate("/programming/treatment/new"),
                  },
                  {
                    label: getLabel("operationTypePHYSICAL"),
                    handler: () => navigate("/programming/physical/new"),
                  },
                  {
                    label: getLabel("operationTypeWINEMAKING"),
                    handler: () => navigate("/programming/winemaking/new"),
                  },
                  {
                    label: getLabel("operationTypeWINEMAKINGPOST"),
                    handler: () => navigate("/programming/winemakingpost/new"),
                  },
                  {
                    label: getLabel("operationTypeBOTTLING"),
                    handler: () => navigate("/programming/bottling/new"),
                  },
                  {
                    label: getLabel("operationTypeCLEANING"),
                    handler: () => navigate("/programming/cleaning/new"),
                  },
                ]}
              ></Dropdown>
            </div>
          )}

          <div className="operation_additional">
            {/* <CTA onClick={requestPDF}>{isMobile ? <Icon icon="fa-print" /> : getLabel("print")}</CTA> */}
            <Dropdown
              label={
                isMobile ? (
                  <Icon icon="fa-print" />
                ) : (
                  <>
                    {getLabel("print")} <Icon icon="fa-print" />
                  </>
                )
              }
              options={[
                {
                  label: getLabel("operationPrintToday"),
                  handler: () => requestPDF(true, false),
                },
                {
                  label: getLabel("operationPrintTodayAndExpired"),
                  handler: () => requestPDF(true),
                },
                {
                  label: getLabel("operationPrintAll"),
                  handler: () => requestPDF(),
                },
              ]}
            />
            <NavLink to="/programming/history" className="history">
              <CTA customClassName="height-100">
                {isMobile ? (
                  <Icon icon="fa-book" />
                ) : (
                  <>
                    {getLabel("showHistory")} <Icon icon="fa-book" />
                  </>
                )}
              </CTA>
            </NavLink>
          </div>
        </div>
        {/* </div> */}
      </>
    );
  };

  const renderInterval = () => {
    return (
      // <div className="primary_container_menu">
      <div className="hub_primary_container_row end">
        <div className="interval_row">
          <div className="interval_value">
            <div className="interval_vwrapper">
              <span>{getLabel("dateFrom")}</span>
              <ReactDatePicker
                selected={dates.startDate}
                onChange={(date) =>
                  setDates({
                    ...dates,
                    startDate: date.setHours(0, 0, 0),
                  })
                }
                selectsStart
                startDate={dates.startDate}
                endDate={dates.endDate}
                minDate={dates.startDate}
                dateFormat={configs.shortDateFormat}
              />
            </div>
            <div className="interval_vwrapper">
              <span>{getLabel("dateTo")}</span>
              <ReactDatePicker
                selected={dates.endDate}
                onChange={(date) =>
                  setDates({
                    ...dates,
                    endDate: date.setHours(23, 59, 59),
                  })
                }
                selectsEnd
                startDate={dates.startDate}
                endDate={dates.endDate}
                minDate={addDays(dates.startDate, 1)}
                dateFormat={configs.shortDateFormat}
              />
            </div>
          </div>
        </div>
      </div>
      // </div>
    );
  };

  const renderList = () => {
    if (operations.length > 0 || expiredOperations?.length > 0) {

      return (
        // <div className="primary_container_menu">
        <div className="hub_primary_container_row">
          <div className="operations_list_table">
            <Suspense fallback={<SkeletonTableEdit colCount={6} />}>
              <TableEdit
                isMobile={isMobile}
                type={"operation"}
                defaultData={[
                  ...expiredOperations?.sort(function (a, b) {
                    let adate = new Date(a?.expire_date),
                      bdate = new Date(b.expire_date),
                      rv = adate - bdate;
                    if (rv === 0) {
                      rv = b?.priority - a?.priority;
                    }
                    if (rv === 0) {
                      rv = a?.id - b?.id;
                    }
                    return rv;
                  }),
                  ...operations
                    ?.filter((o) => new Date() <= new Date(o?.expire_date))
                    ?.sort(function (a, b) {
                      let adate = new Date(a?.expire_date),
                        bdate = new Date(b.expire_date),
                        rv = adate - bdate;
                      if (rv === 0) {
                        rv = b?.priority - a?.priority;
                      }
                      if (rv === 0) {
                        rv = a?.id - b?.id;
                      }
                      return rv;
                    }),
                ]?.filter((o) => new Date(o?.expire_date) >= dates.startDate && new Date(o?.expire_date) <= dates.endDate)}
                // defaultData2={operations?.sort(
                //   (b, a) => new Date(b.expire_date) - new Date(a.expire_date)
                // )}

                columns={columns}
                rowClickHandler={(row) => {
                  navigate(
                    `/programming/${row.original.type?.toLowerCase?.()}/${row.original.id}`
                  );
                }}
                isPagination={true}
                filterType={"operations"}
                isLegend={false}
                groupHandler={(data) =>
                  data?.filter(
                    (o) =>
                      !(
                        o?.repetitive &&
                        Number(o?.relative_id) > 1 &&
                        Number(o?.relative_id) > Number(o?.completed)
                      )
                  )
                }
              />
            </Suspense>
          </div>
        </div>
        // </div>
      );
    }
    return (
      <>
        {(operations.length > 0 || expiredOperations?.length > 0) && (
          <h5>{getLabel("programmedOperations")}</h5>
        )}
        <h5 className="alert_no_info margin_top">
          {getLabel("noOperationsFound")}
        </h5>
      </>
    );
  };

  // const renderExpiredList = () => {
  //   if (expiredOperations.length > 0) {
  //     return (
  //       <div className="hub_primary_container_row">
  //         <div className="operations_list_table">
  //           <h5>{getLabel("expiredOperations")}</h5>
  //           <TableEdit
  //             isMobile={isMobile}
  //             type={"operation"}
  //             // defaultData={expiredOperations?.sort(
  //             //   (b, a) => new Date(b.expire_date) - new Date(a.expire_date)
  //             // )}
  //             defaultData={expiredOperations?.sort(function (a, b) {
  //               let adate = new Date(a?.expire_date),
  //                 bdate = new Date(b.expire_date),
  //                 rv = adate - bdate;
  //               if (rv === 0) {
  //                 rv = b?.priority - a?.priority;
  //               }
  //               return rv;
  //             })}
  //             columns={columns}
  //             rowClickHandler={(row) => {
  //               navigate(
  //                 `/programming/${row.original.type}/${row.original.id}`
  //               );
  //             }}
  //             filterType={"expiredOperations"}
  //             isLegend={false}
  //             groupHandler={(data) =>
  //               data?.filter(
  //                 (o) =>
  //                   !(
  //                     o?.repetitive &&
  //                     Number(o?.relative_id) > 1 &&
  //                     Number(o?.relative_id) > Number(o?.completed)
  //                   )
  //               )
  //             }
  //           />
  //         </div>
  //       </div>
  //     );
  //   }
  //   return;
  // };

  const renderLegend = () => {
    return (
      <div>
        <h5>{getLabel("legend")}</h5>
        <ul>
          {/* <li><span>{getLabel("operationExpired")}</span><span className="icon">&#10071;</span></li> */}
          <li>
            <span>{getLabel("operationExpiringExact")}</span>
            <span className="icon">
              <Icon icon="fa-calendar-day" color="grey" />
            </span>
          </li>
          <li>
            <span>{getLabel("operationAnalysisToBeSampled")}</span>
            <span className="icon">
              <Icon icon="fa-eye-dropper" color="grey" />
            </span>
          </li>
          <li>
            <span>{getLabel("operationAnalysisSampled")}</span>
            <span className="icon">
              <Icon icon="fa-flask-vial" color="grey" />
            </span>
          </li>
          <li>
              <span>{getLabel("operationStatusStarted")}</span>
              <span className="icon">
                <Icon                   icon={"fa-spinner"}
                  // spin
                  style={{ color: "#3498db" }}
                  size={"xs"}
                />
              </span>
          </li>
          <li>
              <span>{getLabel("operationStatusPending")}</span>
              <span className="icon">
                <Icon                   icon={"fa-clock"}
                  color="grey"
                  size={"xs"}
                />
              </span>
          </li>
          {!isMobile && (
            <li>
              <span>{getLabel("filtersOn")}</span>
              <span className="icon red">
                <Icon                   icon="fa-filter"
                  style={{ color: "rgba(243, 239, 245, 1)" }}
                  size={"xs"}
                />
              </span>
            </li>
          )}
          {!isMobile && (
            <li>
              <span>{getLabel("filtersOff")}</span>
              <span className="icon red">
                <Icon                   icon="fa-filter"
                  style={{ color: "rgba(243, 239, 245, 0.5)" }}
                  size={"xs"}
                />
              </span>
            </li>
          )}
          {isMobile && (
            <li>
              <span>{getLabel("operationExpired")}</span>
              <span className="operation expired">
                &nbsp;&nbsp;&nbsp;&nbsp;
              </span>
            </li>
          )}
          {isMobile && (
            <li>
              <span>{getLabel("operationProgrammed")}</span>
              <span className="operation">&nbsp;&nbsp;&nbsp;&nbsp;</span>
            </li>
          )}
        </ul>
      </div>
    );
  };

  const hubTabs = [
    { name: getLabel("hubTabAllOperations"), anchor: "all" },
    { name: getLabel("hubTabOperationsProtocols"), anchor: "protocols" },
  ];

  return (
    <div className="hub_primary_container">
      <div className="hub_primary_container_wrapper">
        <SecondaryBar breadCrumb={[getLabel("programmingNavLink")]} />
        <div className="hub_primary_container_menu">
          {renderNavMenu()}
          <h5>{getLabel("programmedOperations")}</h5>
          <TabSelector tabs={hubTabs} activeTab={activeTab} onTabChange={setActiveTab} />
          {renderInterval()}
          {activeTab === "all" && renderList()}
          {activeTab === "protocols" && (
            <ProtocolApplications
              dateFrom={new Date(dates.startDate)}
              dateTo={new Date(dates.endDate)}
            />
          )}
        </div>
      </div>
      {activeTab === "all" && operations?.length > 0 && (
        <div className="primary_container_lower legend">{renderLegend()}</div>
      )}
    </div>
  );
};

export default OperationsHub;
